import { toastService } from '@/services/toastService';
import { apiService } from './api.js';

/**
 * Cookie utility functions
 */
const cookieUtils = {
  /**
   * Check if cookies are enabled
   * @returns {boolean} True if cookies are enabled
   */
  areEnabled: () => {
    try {
      document.cookie = 'testCookie=1';
      const enabled = document.cookie.indexOf('testCookie=') !== -1;
      document.cookie =
        'testCookie=1; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      return enabled;
    } catch {
      return false;
    }
  },

  /**
   * Set a cookie
   * @param {string} name - Cookie name
   * @param {string} value - Cookie value
   * @param {number} days - Days until expiration
   */
  setCookie: (name, value, days = 7) => {
    try {
      const expires = new Date();
      expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
      document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Strict`;
    } catch (error) {
      console.error(`Error setting cookie ${name}:`, error);
    }
  },

  /**
   * Get a cookie value
   * @param {string} name - Cookie name
   * @returns {string|null} Cookie value or null
   */
  getCookie: name => {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  },

  /**
   * Delete a cookie
   * @param {string} name - Cookie name
   */
  deleteCookie: name => {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  },
};

/**
 * Authentication service for handling user authentication
 */
export const authService = {
  /**
   * Register new user
   * @param {Object} userData - User registration data
   * @param {string} userData.name - User's full name
   * @param {string} userData.email - User's email address
   * @param {string} userData.password - User's password
   * @returns {Promise<Object>} Registration response with token
   */
  register: async userData => {
    // Check if cookies are enabled
    if (!cookieUtils.areEnabled()) {
      const error = new Error(
        'Cookies are disabled. Please enable cookies to use this application.'
      );
      toastService.showError(
        'Cookies are disabled. Please enable cookies to use this application.'
      );
      throw error;
    }

    const response = await apiService.post('/auth/register', userData);

    // Store access token and refresh token if provided
    if (response.accessToken && response.accessToken.token) {
      // Store access token (24 hours expiry to match server JWT)
      cookieUtils.setCookie('accessToken', response.accessToken.token, 1); // 24 hours = 1 day

      // Store refresh token (7 days expiry)
      if (response.refreshToken && response.refreshToken.token) {
        cookieUtils.setCookie('refreshToken', response.refreshToken.token, 7); // 7 days
      }

      // Ensure user data has required fields
      const responseUser = response.user || {};
      const normalizedUser = {
        id: responseUser._id || responseUser.id,
        name: responseUser.name || 'User',
        email: responseUser.email,
        role: responseUser.role || 'user',
        isActive: responseUser.isActive,
        createdAt: responseUser.createdAt,
        updatedAt: responseUser.updatedAt,
        lastLoginAt: responseUser.lastLoginAt,
      };

      cookieUtils.setCookie('user', JSON.stringify(normalizedUser), 7); // 7 days
      toastService.showSuccess(
        'Registration successful! Welcome to Integrable.'
      );
    }

    return response;
  },

  /**
   * Login user
   * @param {Object} credentials - User login credentials
   * @param {string} credentials.email - User's email address
   * @param {string} credentials.password - User's password
   * @returns {Promise<Object>} Login response with tokens
   */
  login: async credentials => {
    try {
      // Check if cookies are enabled
      if (!cookieUtils.areEnabled()) {
        const error = new Error(
          'Cookies are disabled. Please enable cookies to use this application.'
        );
        toastService.showError(
          'Cookies are disabled. Please enable cookies to use this application.'
        );
        throw error;
      }

      const response = await apiService.post('/auth/login', credentials);

      // Store access token and refresh token if provided
      if (response.accessToken && response.accessToken.token) {
        // Store access token (24 hours expiry to match server JWT)
        cookieUtils.setCookie('accessToken', response.accessToken.token, 1); // 24 hours = 1 day

        // Store refresh token (7 days expiry)
        if (response.refreshToken && response.refreshToken.token) {
          cookieUtils.setCookie('refreshToken', response.refreshToken.token, 7); // 7 days
        }

        // Ensure user data has required fields
        const userData = response.user || {};
        const normalizedUser = {
          id: userData._id || userData.id,
          name: userData.name || 'User',
          email: userData.email,
          role: userData.role || 'user',
          isActive: userData.isActive,
          createdAt: userData.createdAt,
          updatedAt: userData.updatedAt,
          lastLoginAt: userData.lastLoginAt,
        };

        cookieUtils.setCookie('user', JSON.stringify(normalizedUser), 7); // 7 days
        toastService.showSuccess(`Welcome back, ${normalizedUser.name}!`);
      } else {
        console.warn('authService: No access token received in response');
      }

      return response;
    } catch (error) {
      console.error('authService: Login API call failed', error);
      throw error;
    }
  },

  /**
   * Request password reset
   * @param {string} email - User's email address
   * @returns {Promise<Object>} Password reset response
   */
  forgotPassword: async email => {
    const response = await apiService.post('/auth/forgot-password', {
      email,
    });
    return response;
  },

  /**
   * Reset password with OTP
   * @param {Object} resetData - Password reset data
   * @param {string} resetData.email - User's email address
   * @param {string} resetData.otp - OTP code
   * @param {string} resetData.password - New password
   * @param {string} resetData.confirmPassword - Confirm password
   */
  resetPasswordWithOTP: async resetData => {
    const response = await apiService.post('/auth/reset-password', resetData);
    toastService.showSuccess(
      'Password reset successful. You can now login with your new password.'
    );
    return response;
  },

  /**
   * Request email update
   * @param {string} newEmail
   * @param {string} currentPassword
   * @returns {Promise<Object>}
   */
  requestEmailUpdate: async (newEmail, currentPassword) => {
    try {
      const response = await apiService.post('/auth/email/request-update', {
        newEmail,
        currentPassword,
      });
      toastService.showSuccess('OTP sent to new email successfully');
      return response;
    } catch (error) {
      console.error('authService: Request email update failed', error);
      throw error;
    }
  },

  /**
   * Verify email update OTP
   * @param {string} newEmail
   * @param {string} otp
   * @returns {Promise<Object>}
   */
  verifyEmailUpdate: async (newEmail, otp) => {
    try {
      const response = await apiService.post('/auth/email/verify-update', {
        newEmail,
        otp,
      });

      if (response.success && response.user) {
        const updatedUser = response.user;
        const normalizedUser = {
          id: updatedUser._id || updatedUser.id,
          name: updatedUser.name || 'User',
          email: updatedUser.email,
          role: updatedUser.role || 'user',
          isActive: updatedUser.isActive,
          createdAt: updatedUser.createdAt,
          updatedAt: updatedUser.updatedAt,
          lastLoginAt: updatedUser.lastLoginAt,
        };
        cookieUtils.setCookie('user', JSON.stringify(normalizedUser), 7);
        toastService.showSuccess(
          response.message || 'Email updated successfully'
        );
      }

      return response;
    } catch (error) {
      console.error('authService: Verify email update failed', error);
      throw error;
    }
  },

  /**
   * Logout user
   */
  logout: () => {
    cookieUtils.deleteCookie('accessToken');
    cookieUtils.deleteCookie('refreshToken');
    cookieUtils.deleteCookie('user');

    // Dispatch custom events to notify other parts of the app (like AuthContext)
    window.dispatchEvent(new CustomEvent('auth-state-changed'));
    window.dispatchEvent(new CustomEvent('auth-logout'));

    toastService.showSuccess('Logged out successfully');

    // Navigate to login page
    window.location.href = '/login';
  },

  /**
   * Get current user from cookies
   * @returns {Object|null} Current user object or null
   */
  getCurrentUser: () => {
    try {
      const user = cookieUtils.getCookie('user');
      if (!user) {
        return null;
      }

      const parsedUser = JSON.parse(user);
      return parsedUser;
    } catch (error) {
      console.error(
        'authService: Error parsing user data from cookies:',
        error
      );
      // Clear corrupted user data
      cookieUtils.deleteCookie('user');
      return null;
    }
  },

  /**
   * Check if user is authenticated
   * @returns {boolean} True if user is authenticated
   */
  isAuthenticated: () => {
    const token = cookieUtils.getCookie('accessToken');
    const hasToken = !!token;
    return hasToken;
  },

  /**
   * Get access token
   * @returns {string|null} Access token or null
   */
  getToken: () => {
    return cookieUtils.getCookie('accessToken');
  },

  /**
   * Get refresh token
   * @returns {string|null} Refresh token or null
   */
  getRefreshToken: () => {
    return cookieUtils.getCookie('refreshToken');
  },

  /**
   * Refresh access token using refresh token
   * @returns {Promise<Object>} Refresh response
   */
  refreshToken: async () => {
    try {
      const refreshToken = cookieUtils.getCookie('refreshToken');
      if (!refreshToken) {
        console.warn('authService: No refresh token found, logging out');
        authService.logout();
        throw new Error('No refresh token available');
      }

      // Call refresh API directly using fetch to avoid recursion in apiService
      // or use a flag in apiService to skip refresh for this call
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/auth/refresh-token`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refreshToken }),
        }
      );

      const data = await response.json();

      if (response.status === 401) {
        console.warn(
          'authService: Refresh token expired or invalid (401), logging out'
        );
        authService.logout();
        throw new Error('Session expired. Please login again.');
      }

      if (!response.ok) {
        throw new Error(data.message || 'Failed to refresh token');
      }

      if (data.accessToken) {
        // Store new access token (24 hours expiry to match server JWT)
        cookieUtils.setCookie('accessToken', data.accessToken, 1);
        return data;
      } else {
        throw new Error('No access token received in refresh response');
      }
    } catch (error) {
      console.error('authService: Token refresh failed', error);
      // If not already handled by 401 check, logout on other errors too if it's a connectivity/auth issue
      if (
        error.message.includes('No refresh token') ||
        error.message.includes('expired')
      ) {
        authService.logout();
      }
      throw error;
    }
  },

  /**
   * Update user profile
   * @param {Object} userData - Data to update (name, email)
   * @returns {Promise<Object>} Update response with updated user
   */
  updateProfile: async userData => {
    try {
      const response = await apiService.put('/auth/me', userData);

      if (response.success && response.user) {
        // Ensure user data has required fields
        const updatedUser = response.user;
        const normalizedUser = {
          id: updatedUser._id || updatedUser.id,
          name: updatedUser.name || 'User',
          email: updatedUser.email,
          role: updatedUser.role || 'user',
          isActive: updatedUser.isActive,
          createdAt: updatedUser.createdAt,
          updatedAt: updatedUser.updatedAt,
          lastLoginAt: updatedUser.lastLoginAt,
        };

        // Update user cookie (7 days expiry)
        cookieUtils.setCookie('user', JSON.stringify(normalizedUser), 7);
        toastService.showSuccess('Profile updated successfully');
      }

      return response;
    } catch (error) {
      console.error('authService: Profile update failed', error);
      throw error;
    }
  },

  /**
   * Debug function to list all cookies
   * @returns {Object} Object containing all cookies
   */
  debugCookies: () => {
    const cookies = {};
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf('=') !== -1) {
        const parts = c.split('=');
        cookies[parts[0]] = parts[1];
      }
    }
    return cookies;
  },
};

export default authService;
